import React from "react";
import { Pencil, Waypoints, Wrench } from "lucide-react";
import { useViewport } from "@/shared/hooks/useViewport";
import BottomSheet from "@/shared/components/responsive/BottomSheet";
import SideSheet from "@/shared/components/responsive/SideSheet";
import { CloseButton, GhostButton, ScoreBar } from "@/shared/components/ui-kit";
import { fmtMs, fmtCost } from "@/shared/lib/runs";
import { CheckRow, StatusPill, DeltaChip, TagChip } from "./atoms";

import type { ReactNode } from "react";

import type { EvalCase, EvalCaseOrigin, EvalResult } from "@/shared/types";

/** What the header's close button and the footer's edit button do, handed to
    the body together. */
interface SheetHandlers {
  close: () => void;
  edit: () => void;
}

/* Screen 3 — one case inside one run.

   The point of this sheet is the evidence column: for every check, the quote or
   the tool sequence that decided the score. A score with no evidence is a number
   people learn to ignore. teams/ReceiptDialog.jsx renders handoff verification
   the same way; this is deliberately the same shape. */

function Block({ label, children, mono = false, tone = "default" }: {
  label: ReactNode;
  children?: ReactNode;
  mono?: boolean;
  tone?: "default" | "danger";
}) {
  return (
    <div className="mb-4">
      <div className="section-label mb-1.5">{label}</div>
      <div
        className={`px-3 py-2.5 rounded-[10px] text-[13px] leading-[1.6] whitespace-pre-wrap break-words ${
          mono ? "font-mono text-[12px]" : ""
        } ${
          tone === "danger"
            ? "bg-[var(--danger-bg)] ring-1 ring-[var(--danger-ring)] text-[var(--text-primary)]"
            : "bg-[var(--surface-sunk)] ring-1 ring-[var(--hairline)] text-[var(--text-secondary)]"
        }`}
      >
        {children}
      </div>
    </div>
  );
}

function Body({ kase, result, baseResult, onEdit, onOpenTrace }: {
  kase: EvalCase;
  result: EvalResult;
  baseResult: EvalResult | null | undefined;
  onEdit: SheetHandlers;
  onOpenTrace: (origin: EvalCaseOrigin) => void;
}) {
  const delta = baseResult && result.score != null && baseResult.score != null
    ? +(result.score - baseResult.score).toFixed(3)
    : null;

  return (
    <div className="h-full flex flex-col min-h-0">
      <div className="shrink-0 flex items-start gap-2 px-4 pt-4 pb-3 border-b hairline">
        <div className="flex-1 min-w-0">
          <div className="text-[14.5px] font-semibold tracking-[-0.01em] text-[var(--text-primary)]">{kase.name}</div>
          <div className="flex items-center gap-1.5 flex-wrap mt-1.5">
            <StatusPill status={result.status} />
            <ScoreBar value={result.score} target={0.7} width={44} />
            {delta != null && <DeltaChip delta={delta} />}
            {(kase.tags || []).map((t) => <TagChip key={t} tag={t} />)}
          </div>
        </div>
        <CloseButton onClick={onEdit.close} />
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto px-4 py-4">
        <Block label="Input">{kase.input}</Block>

        {kase.expected ? <Block label="Expected">{kase.expected}</Block> : null}

        {result.error && <Block label="Why there is no result" tone="danger">{result.error}</Block>}

        <Block label="Output" tone={result.status === "fail" ? "danger" : "default"}>
          {result.status === "pending"
            ? "Still running — the result appears when this case's run ends."
            : result.output || <span className="italic text-[var(--text-quaternary)]">No answer</span>}
        </Block>

        {baseResult && baseResult.output !== result.output && (
          <Block label={`Baseline output`}>{baseResult.output}</Block>
        )}

        {/* Trajectory, stated plainly. This is the half of an agent eval that a
            plain LLM eval tool has no way to show. */}
        <div className="mb-4">
          <div className="section-label mb-1.5">Trajectory</div>
          <div className="px-3 py-2.5 rounded-[10px] bg-[var(--surface-sunk)] ring-1 ring-[var(--hairline)]">
            <div className="flex items-center gap-1.5 flex-wrap">
              {result.tools.length ? (
                result.tools.map((t, i) => {
                  // Arguments on the pill, not just the tool name — a trajectory
                  // that names only the tools can't show why an argument check failed.
                  const args = result.toolCalls?.[i]?.args;
                  const argText = args && Object.keys(args).length
                    ? Object.entries(args).map(([k, v]) => `${k}=${typeof v === "string" ? v : JSON.stringify(v)}`).join(", ")
                    : null;
                  return (
                    <React.Fragment key={`${t}-${i}`}>
                      {i > 0 && <span className="text-[var(--text-quaternary)] text-[11px]">→</span>}
                      <span
                        title={argText ? `${t}(${argText})` : t}
                        className="inline-flex items-center gap-1 h-[19px] px-2 rounded-full bg-[var(--surface)] ring-1 ring-[var(--hairline)] font-mono text-[11px] text-[var(--text-secondary)] max-w-full"
                      >
                        <Wrench className="w-2.5 h-2.5 shrink-0" strokeWidth={1.75} />
                        <span className="truncate">
                          {t}
                          {argText && <span className="text-[var(--text-quaternary)]">({argText})</span>}
                        </span>
                      </span>
                    </React.Fragment>
                  );
                })
              ) : (
                <span className="text-[12.5px] text-[var(--text-quaternary)] italic">No tools called</span>
              )}
            </div>
            <div className="flex items-center gap-3 mt-2.5 pt-2.5 border-t hairline font-mono text-[11.5px] tabular-nums text-[var(--text-tertiary)]">
              <span>{result.steps} steps</span>
              <span>{fmtMs(result.ms)}</span>
              <span>{fmtCost(result.cost)}</span>
            </div>
          </div>
        </div>

        <div className="section-label mb-1.5">Checks · {result.checks.length}</div>
        <div className="rounded-[10px] bg-[var(--surface)] ring-1 ring-[var(--hairline)] px-3.5 py-1 divide-y divide-[var(--hairline)]">
          {result.checks.map((c) => <CheckRow key={c.id} result={c} />)}
        </div>
      </div>

      <div className="shrink-0 flex items-center gap-2 px-4 py-3 border-t hairline">
        <GhostButton onClick={onEdit.edit}>
          <Pencil className="w-3.5 h-3.5" strokeWidth={1.75} /> Edit case
        </GhostButton>
        <div className="flex-1" />
        {result.runId && (
          <GhostButton onClick={() => onOpenTrace({ runId: result.runId as string, spanId: null })} title="Open the run this case produced">
            <Waypoints className="w-3.5 h-3.5" strokeWidth={1.75} /> Trace
          </GhostButton>
        )}
        {kase.origin && (
          // Rendered only when the case HAS an origin — the guard is one line up.
          <GhostButton onClick={() => onOpenTrace(kase.origin as EvalCaseOrigin)} title="Open the run this case came from">
            <Waypoints className="w-3.5 h-3.5" strokeWidth={1.75} /> Source trace
          </GhostButton>
        )}
      </div>
    </div>
  );
}

export default function CaseSheet({ open, onOpenChange, kase, result, baseResult, onEdit, onOpenTrace }: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  kase: EvalCase | null | undefined;
  result: EvalResult | null | undefined;
  baseResult: EvalResult | null | undefined;
  onEdit: (kase: EvalCase) => void;
  onOpenTrace: (origin: EvalCaseOrigin) => void;
}) {
  const { isDesktop } = useViewport();
  if (!kase || !result) return null;

  const handlers: SheetHandlers = { close: () => onOpenChange(false), edit: () => { onOpenChange(false); onEdit(kase); } };
  const body = (
    <Body kase={kase} result={result} baseResult={baseResult} onEdit={handlers} onOpenTrace={onOpenTrace} />
  );

  return isDesktop ? (
    <SideSheet open={open} onOpenChange={onOpenChange} side="right" width={480} title={kase.name}>
      {body}
    </SideSheet>
  ) : (
    <BottomSheet open={open} onOpenChange={onOpenChange}>
      <div className="h-full">{body}</div>
    </BottomSheet>
  );
}
